import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import ForkliftIcon from "@mui/icons-material/Agriculture";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import StoreMallDirectoryRoundedIcon from "@mui/icons-material/StoreMallDirectoryRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { selectUser } from "../../features/userSlice";
import { Card, PageHeader, PageShell, SectionHeader } from "../../components/UI/Page";
import { LoadingState, StatTile } from "../../components/UI/States";
import {
  InspectionDialog,
  Notice,
  ResultChip,
  assetName,
  serviceSummary,
} from "../../components/Fleet/FleetParts";
import {
  HISTORY_DAYS,
  formatDateTime,
  relativeDay,
  startOfLocalDay,
} from "../../components/Fleet/fleetFormat";
import { fetchFleetOverview } from "./fleetApi";

// Fleet landing page: today's numbers, what's been flagged this week, and
// the way into each part of the fleet.

const NavCard = ({ to, icon: Icon, title, description, stat }) => (
  <Link
    to={to}
    className="app-card group flex items-center gap-4 rounded-2xl border p-4 transition hover:-translate-y-0.5 hover:border-teal-400 sm:p-5"
  >
    <span className="app-icon-tile h-11 w-11 shrink-0 rounded-2xl bg-teal-500/15 text-teal-600 dark:text-teal-300">
      <Icon />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block font-semibold">{title}</span>
      <span className="app-muted block text-sm">{description}</span>
      {stat && <span className="mt-1 block text-xs font-semibold text-teal-700 dark:text-teal-300">{stat}</span>}
    </span>
    <ChevronRightRoundedIcon className="app-muted shrink-0 transition group-hover:translate-x-0.5" />
  </Link>
);

const FleetHome = () => {
  const user = useSelector(selectUser);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    fetchFleetOverview(user.id, startOfLocalDay(0))
      .then(setOverview)
      .catch((e) => setError(e.message));
  }, [user?.id]);

  const f = overview?.forklifts;
  const v = overview?.vehicles;

  return (
    <PageShell size="2xl">
      <InspectionDialog entry={open} onClose={() => setOpen(null)} userId={user?.id} showAssetLink />

      <PageHeader
        icon={LocalShippingRoundedIcon}
        tone="teal"
        eyebrow="Fleet"
        title="Fleet"
        description={`Forklifts, trucks, and ${HISTORY_DAYS} days of inspections from the mobile app and branch kiosks.`}
      />

      {error && <Notice tone="rose">{error}</Notice>}
      {overview?.gpsError && <Notice>{overview.gpsError}</Notice>}

      <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <NavCard
          to="/fleet/forklifts"
          icon={ForkliftIcon}
          title="Forklifts"
          description="Every unit, its status and check-ins."
          stat={f && `${f.total} units · ${f.inService} in service`}
        />
        <NavCard
          to="/fleet/vehicles"
          icon={LocalShippingRoundedIcon}
          title="Vehicles"
          description="Trucks with live GPS and pre-trip inspections."
          stat={v && `${v.total} trucks · ${v.running} running now`}
        />
        <NavCard
          to="/fleet/inspections"
          icon={FactCheckRoundedIcon}
          title="Inspection Log"
          description={`Search ${HISTORY_DAYS} days of every inspection.`}
          stat={overview && `${overview.inspections.week.toLocaleString()} in the last 7 days`}
        />
        <NavCard
          to="/fleet/forks/locationmanager"
          icon={StoreMallDirectoryRoundedIcon}
          title="Location Manager"
          description="Branches, their contacts, shifts, and forklifts."
        />
      </div>

      {!overview && !error && <LoadingState label="Loading the fleet…" />}

      {overview && (
        <>
          <SectionHeader title="Today" />
          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile
              label="Forklifts checked in"
              value={`${f.inspectedToday} / ${f.inService}`}
              hint="Of the units in service"
              tone={f.inspectedToday >= f.inService ? "emerald" : "default"}
            />
            <StatTile
              label="Trucks inspected"
              value={v ? `${v.inspectedToday} / ${v.total}` : "—"}
              hint="Pre-trip inspections"
            />
            <StatTile label="Inspections today" value={overview.inspections.today} hint="Forklifts and trucks" />
            <StatTile
              label="Flagged this week"
              value={overview.inspections.flaggedWeek}
              hint="Needs attention or not safe"
              tone={overview.inspections.flaggedWeek ? "amber" : "default"}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="app-card overflow-hidden rounded-2xl border lg:col-span-2">
              <div className="flex items-center justify-between gap-3 px-4 pt-4 sm:px-5">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight">Flagged in the last 7 days</h2>
                  <p className="app-muted text-sm">Inspections with an item that needs attention or a Not Safe report.</p>
                </div>
                <Link to="/fleet/inspections?result=flagged" className="app-link shrink-0 text-sm font-semibold">
                  See all
                </Link>
              </div>
              {overview.flagged.length === 0 ? (
                <p className="app-muted flex items-center gap-2 px-5 py-8 text-sm">
                  <CheckCircleRoundedIcon className="text-emerald-600 dark:text-emerald-400" fontSize="small" />
                  Nothing flagged this week.
                </p>
              ) : (
                <ul className="mt-3 divide-y divide-[color:var(--app-divider)] border-t app-divider">
                  {overview.flagged.map((entry) => (
                    <li key={entry.key}>
                      <button
                        type="button"
                        onClick={() => setOpen(entry)}
                        className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-[color:var(--app-hover)] sm:px-5"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold">{assetName(entry)}</span>
                            <ResultChip result={entry.result} />
                          </span>
                          <span className="block text-sm">
                            {entry.flagged.map((item) => item.label).join(", ") || "Reported"}
                          </span>
                          <span className="app-muted block text-xs tabular-nums">
                            {entry.branch} · {entry.inspector} · {formatDateTime(entry.at)}
                          </span>
                        </span>
                        <ChevronRightRoundedIcon className="app-muted mt-1 shrink-0" fontSize="small" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-6">
              <Card>
                <SectionHeader
                  title="Out of service"
                  description={`${f.outOfService} forklift${f.outOfService === 1 ? "" : "s"}${
                    v ? ` · ${v.outOfService} truck${v.outOfService === 1 ? "" : "s"}` : ""
                  }`}
                />
                {overview.outOfService.length === 0 && !overview.outOfServiceVehicles?.length ? (
                  <p className="app-muted text-sm">Everything is in service.</p>
                ) : (
                  <ul className="space-y-1">
                    {(overview.outOfServiceVehicles || []).map((truck) => (
                      <li key={`v-${truck.key}`}>
                        <Link
                          to={`/fleet/vehicles/${truck.key}`}
                          className="-mx-2 block rounded-lg px-2 py-1.5 text-sm hover:bg-[color:var(--app-hover)]"
                        >
                          <span className="font-semibold tabular-nums">Truck {truck.label}</span>
                          <span className="app-muted"> · {truck.branch || "No branch"}</span>
                          <span className="app-muted block truncate text-xs">{serviceSummary(truck.service)}</span>
                        </Link>
                      </li>
                    ))}
                    {overview.outOfService.map((unit) => (
                      <li key={unit.id}>
                        <Link
                          to={`/fleet/forklifts/${unit.id}`}
                          className="-mx-2 block rounded-lg px-2 py-1.5 text-sm hover:bg-[color:var(--app-hover)]"
                        >
                          <span className="font-semibold tabular-nums">Forklift #{unit.unit}</span>
                          <span className="app-muted"> · {unit.location || "No branch"}</span>
                          <span className="app-muted block truncate text-xs">
                            {serviceSummary(unit.service) || relativeDay(unit.stats.lastAt || unit.lastChecked)}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm font-semibold">
                  {f.outOfService > 0 && (
                    <Link to="/fleet/forklifts?status=out" className="app-link">
                      All forklifts out
                    </Link>
                  )}
                  {v?.outOfService > 0 && (
                    <Link to="/fleet/vehicles?status=oos" className="app-link">
                      All trucks out
                    </Link>
                  )}
                </div>
              </Card>

              <Card>
                <SectionHeader title="Needs a look" />
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link to="/fleet/forklifts?status=quiet" className="flex items-center justify-between gap-2 hover:text-[color:var(--app-accent)]">
                      <span>Forklifts with no check-in for 7+ days</span>
                      <span className="font-semibold tabular-nums">{f.quiet}</span>
                    </Link>
                  </li>
                  {v && (
                    <li>
                      <Link to="/fleet/vehicles?status=notToday" className="flex items-center justify-between gap-2 hover:text-[color:var(--app-accent)]">
                        <span>Trucks not inspected today</span>
                        <span className="font-semibold tabular-nums">{v.total - v.inspectedToday}</span>
                      </Link>
                    </li>
                  )}
                  <li>
                    <Link to="/fleet/inspections?result=missed" className="flex items-center justify-between gap-2 hover:text-[color:var(--app-accent)]">
                      <span>Trucks driven with no inspection (7 days)</span>
                      <span className="font-semibold tabular-nums">{overview.inspections.missedWeek ?? 0}</span>
                    </Link>
                  </li>
                  {v && (
                    <li>
                      <Link to="/fleet/vehicles?status=noGps" className="flex items-center justify-between gap-2 hover:text-[color:var(--app-accent)]">
                        <span>Trucks without GPS</span>
                        <span className="font-semibold tabular-nums">{v.total - v.tracked}</span>
                      </Link>
                    </li>
                  )}
                </ul>
              </Card>
            </div>
          </div>
        </>
      )}
    </PageShell>
  );
};

export default FleetHome;
